import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  DocumentCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserCircleIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  BeakerIcon,
  IdentificationIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useSelectedPatient } from '../hooks/useSelectedPatient';
import { useAuth } from '../hooks/useAuth';
import { useCertificateForm } from '../hooks/formHooks/useCertificateForm';
import { usePrescriptionForm } from '../hooks/formHooks/usePrescriptionForm';
import type { PrescriptionFormData } from '../hooks/formHooks/usePrescriptionForm';
import { usePdfGeneration } from '../hooks/usePdfGeneration';
import { getDoctors, getDoctorSignatureUrl } from '../services/api/apis';
import type { Patient, Doctor, CertificateFormData } from '../types';
import { CERTIFICATE_TYPES } from '../types';
import { buildCertificateContentBox } from '../services/pdf/pdfInputBuilder';
import { getBase64FromUrl, getPatientPronouns, patientNameWithTitle } from '../helpers';
// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  pending:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  issued:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

// ─── Shared primitives ────────────────────────────────────────────────────────

const inputCls = (hasError?: string) =>
  `w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${
    hasError
      ? 'border-red-400 focus:ring-red-300 bg-red-50 dark:bg-red-900/10'
      : 'border-gray-200 dark:border-gray-700 focus:ring-blue-400 bg-white dark:bg-gray-800'
  } text-gray-900 dark:text-white placeholder-gray-400`;

const dateCls = (hasError?: string) =>
  `${inputCls(hasError)} [color-scheme:light] dark:[color-scheme:dark]`;

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, error, required, hint, className = '', children }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

interface SectionProps {
  step: number;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  accent?: 'blue' | 'emerald';
}

const Section: React.FC<SectionProps> = ({ step, title, icon, children, accent = 'blue' }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
      <span className={`w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${accent === 'emerald' ? 'bg-emerald-600' : 'bg-blue-600'}`}>
        {step}
      </span>
      <span className="text-gray-500 dark:text-gray-400">{icon}</span>
      <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
        {title}
      </h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ─── Yes/No toggle ────────────────────────────────────────────────────────────

const YesNoToggle: React.FC<{
  value: 'Yes' | 'No';
  onChange: (v: 'Yes' | 'No') => void;
}> = ({ value, onChange }) => (
  <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-fit flex-shrink-0">
    {(['Yes', 'No'] as const).map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`px-3 py-1 text-xs font-semibold transition-colors ${
          value === opt
            ? opt === 'Yes'
              ? 'bg-emerald-500 text-white'
              : 'bg-red-400 text-white'
            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

// ─── History row (label + YesNo + optional detail input) ─────────────────────

const HistoryRow: React.FC<{
  label: string;
  value: 'Yes' | 'No';
  onToggle: (v: 'Yes' | 'No') => void;
  detailName: string;
  detailValue: string;
  onDetailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  detailPlaceholder?: string;
}> = ({ label, value, onToggle, detailName, detailValue, onDetailChange, detailPlaceholder }) => (
  <div className="flex flex-wrap items-center gap-3 py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
    <span className="text-sm text-gray-700 dark:text-gray-200 min-w-[240px]">{label}</span>
    <YesNoToggle value={value} onChange={onToggle} />
    {value === 'Yes' && (
      <input
        name={detailName}
        value={detailValue}
        onChange={onDetailChange}
        placeholder={detailPlaceholder || 'Specify details…'}
        className="flex-1 min-w-[160px] px-3 py-1 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
      />
    )}
  </div>
);

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div className="flex items-center gap-4">
      <div className="w-9 h-9 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="space-y-2">
        <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-3 w-32 bg-gray-100 dark:bg-gray-800 rounded" />
      </div>
    </div>
    <div className="h-28 bg-gray-200 dark:bg-gray-700 rounded-xl" />
    <div className="grid grid-cols-2 gap-6">
      <div className="h-56 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      <div className="h-56 bg-gray-200 dark:bg-gray-700 rounded-xl" />
    </div>
    <div className="h-52 bg-gray-200 dark:bg-gray-700 rounded-xl" />
  </div>
);

// ─── Patient info banner ──────────────────────────────────────────────────────

const PatientBanner: React.FC<{ patient: Patient; displayName: string }> = ({ patient, displayName }) => (
  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800 px-6 py-4">
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      <div>
        <p className="text-xs text-blue-500 dark:text-blue-400 font-semibold uppercase tracking-wide">Patient</p>
        <p className="text-base font-bold text-gray-900 dark:text-white mt-0.5">{displayName}</p>
      </div>
      <div className="h-10 w-px bg-blue-200 dark:bg-blue-700 hidden sm:block" />
      <div>
        <p className="text-xs text-blue-500 dark:text-blue-400 font-semibold uppercase tracking-wide">Doc ID</p>
        <p className="text-sm font-mono font-medium text-gray-700 dark:text-gray-200 mt-0.5">#{patient.doc_id}</p>
      </div>
      <div className="h-10 w-px bg-blue-200 dark:bg-blue-700 hidden sm:block" />
      <div>
        <p className="text-xs text-blue-500 dark:text-blue-400 font-semibold uppercase tracking-wide">Age / Gender</p>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-0.5">
          {patient.age || '—'} / {patient.gender || '—'}
        </p>
      </div>
      <div className="h-10 w-px bg-blue-200 dark:bg-blue-700 hidden sm:block" />
      <div>
        <p className="text-xs text-blue-500 dark:text-blue-400 font-semibold uppercase tracking-wide">Status</p>
        <span className={`inline-flex mt-0.5 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[patient.status] || 'bg-gray-100 text-gray-700'}`}>
          {patient.status}
        </span>
      </div>
      {patient.certificate_type && (
        <>
          <div className="h-10 w-px bg-blue-200 dark:bg-blue-700 hidden sm:block" />
          <div>
            <p className="text-xs text-blue-500 dark:text-blue-400 font-semibold uppercase tracking-wide">Certificate Type</p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-0.5">{patient.certificate_type}</p>
          </div>
        </>
      )}
    </div>
  </div>
);

// ─── Main inner form (mounts only after patient loads) ────────────────────────

type ActiveTab = 'certificate' | 'prescription';

interface CertificateFormInnerProps {
  patient: Patient;
  initialFormData: Partial<CertificateFormData>;
  displayName: string;
  isAdmin?: boolean;
  isPublic?: boolean;
  submittedPrescription?: PrescriptionFormData | null;
}

export const CertificateFormInner: React.FC<CertificateFormInnerProps> = ({
  patient,
  initialFormData,
  displayName,
  isAdmin = true,
  isPublic = false,
  submittedPrescription = null,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('certificate');
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [certSaveSuccess, setCertSaveSuccess] = useState(false);
  const [rxSaveSuccess, setRxSaveSuccess] = useState(false);
  const [showPdfPanel, setShowPdfPanel] = useState(false);
  const [includePrescription, setIncludePrescription] = useState(false);
  const [isSignatureFetching, setIsSignatureFetching] = useState(false);
  const [isContentEditing, setIsContentEditing] = useState(false);

  const {
    certTemplateReady,
    rxTemplateReady,
    isLoadingTemplates,
    templateError,
    isGenerating,
    generateError,
    uploadState,
    uploadError,
    pendingUpload,
    fetchTemplates,
    generateCertificatePdf,
    generateMergedPdf,
    confirmUpload,
    discardUpload,
  } = usePdfGeneration();

  // ── Certificate form ──────────────────────────────────────────────────────
  const {
    formData,
    errors,
    isDirty: certIsDirty,
    isSubmitting: certIsSubmitting,
    submitError: certSubmitError,
    handleChange,
    setField,
    reset: certReset,
    submit: certSubmit,
  } = useCertificateForm({
    patientDocId: patient.doc_id,
    initialData: initialFormData,
    onSubmitSuccess: () => {
      setCertSaveSuccess(true);
      setTimeout(() => setCertSaveSuccess(false), 3500);
    },
  });

  // ── Prescription initial data ─────────────────────────────────────────────
  // When the doctor has submitted a prescription, use it directly (full data).
  // Otherwise fall back to deriving non-clinical fields from patient metadata.
  const prescriptionInitialData = useMemo((): Partial<PrescriptionFormData> => {
    if (submittedPrescription) {
      return {
        ...submittedPrescription,
        // Prefer the freshly-fetched signature URL over whatever the doctor stored
        signature: initialFormData.signature || submittedPrescription.signature || '',
      };
    }
    let illnessData: Partial<PrescriptionFormData> = {};
    if (patient.detail_of_illness) {
      try {
        illnessData = JSON.parse(patient.detail_of_illness);
      } catch {
        illnessData = { complaintsOf: patient.detail_of_illness };
      }
    }
    return {
      drName: initialFormData.drName || '',
      regNo: initialFormData.regNo || '',
      specialization: initialFormData.specialization || '',
      patientName: initialFormData.patientName || '',
      docId: initialFormData.docId || '',
      ageGender: [initialFormData.age, initialFormData.gender].filter(Boolean).join(' / '),
      issueDate: initialFormData.issueDate || new Date().toISOString().split('T')[0],
      fullDiagnosis: initialFormData.diagnosis || '',
      signature: initialFormData.signature || '',
      ...illnessData,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Prescription form ─────────────────────────────────────────────────────
  const {
    formData: rxData,
    isDirty: rxIsDirty,
    isSubmitting: rxIsSubmitting,
    submitError: rxSubmitError,
    handleChange: rxHandleChange,
    setField: rxSetField,
    setRxLine,
    addRxLine,
    removeRxLine,
    reset: rxReset,
    submit: rxSubmit,
  } = usePrescriptionForm({
    patientDocId: patient.doc_id,
    initialData: prescriptionInitialData,
    submittedData: submittedPrescription ?? undefined,
    persistDraft: true,
    onSubmitSuccess: () => {
      setRxSaveSuccess(true);
      setTimeout(() => setRxSaveSuccess(false), 3500);
    },
  });

  useEffect(() => {
    getDoctors().then(setDoctors).catch(() => {});
  }, []);

  useEffect(() => {
    if (formData.certificateType === "FITNESS CERTIFICATE") {
      (async () => {
        try {
          const stampBase64 = await getBase64FromUrl('/fit.png');
          console.log('Loaded fitness stamp image, setting to form field');
          setField('fitStamp', stampBase64);
        } catch {
          console.warn('Failed to load fitness stamp image');
        }
        
      })();
    }
    else{
      setField('fitStamp', '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.certificateType]);

  // When a doctor is picked, auto-fill both forms and fetch signature
  const handleDoctorSelect = (doctorId: string) => {
    const doc = doctors.find((d) => d.id === doctorId);
    if (!doc) return;
    setField('drName', doc.name);
    setField('regNo', doc.regNo);
    setField('specialization', doc.specialization);
    rxSetField('drName', doc.name);
    rxSetField('regNo', doc.regNo);
    rxSetField('specialization', doc.specialization);

    setIsSignatureFetching(true);
    setField('signature', '');
    (async () => {
      try {
        const url = await getDoctorSignatureUrl(doctorId);
        if (url) {
          const base64 = await getBase64FromUrl(url);
          setField('signature', base64);
        }
      } catch { /* no signature — silent */ } finally {
        setIsSignatureFetching(false);
      }
    })();
  };

  const activeDoctorId = doctors.find((d) => d.name === formData.drName)?.id ?? '';

  // Auto-fill pronouns whenever gender changes (only if user hasn't customised them)
  useEffect(() => {
    if (!formData.gender) return;
    const { he_she, his_her, him_her } = getPatientPronouns(formData.gender);
    setField('he_she', he_she);
    setField('his_her', his_her);
    setField('him_her', him_her);
    setField('patientName', patientNameWithTitle(formData.gender, formData.patientName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.gender]);

  // Live preview — recomputes whenever any content-relevant field changes
  const generatedContent = useMemo(
    () => buildCertificateContentBox(formData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formData.certificateType, formData.gender, formData.drName, formData.patientName,
      formData.guardianName, formData.age, formData.identificationMark, formData.address,
      formData.examTime, formData.examDate, formData.diagnosis, formData.noOfDays,
      formData.fromDate, formData.caretakerFullName, formData.caretakerRelation,
      formData.caretakerAge, formData.he_she, formData.his_her,
    ]
  );

  // Reset manual edit flag whenever the certificate type changes
  useEffect(() => {
    setIsContentEditing(false);
  }, [formData.certificateType]);
  useEffect(() => {
  rxSetField('patientName', formData.patientName);
}, [formData.patientName, rxSetField]);

  // Sync auto-generated content into formData.content while not manually editing
  useEffect(() => {
    if (isContentEditing || !formData.certificateType) return;
    if (formData.content !== generatedContent) {
      setField('content', generatedContent);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedContent, isContentEditing, formData.certificateType]);

  const handleCertContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setIsContentEditing(true);
    setField('content', e.target.value);
  }, [setField]);

  const resetCertToAuto = useCallback(() => {
    setIsContentEditing(false);
    setField('content', generatedContent);
  }, [generatedContent, setField]);

  const handleGeneratePdf = useCallback(() => {
    if (!certTemplateReady) return;
    const rxDataForPdf = {
      ...rxData,
      ageGender: [formData.age, formData.gender].filter(Boolean).join(' / ') || rxData.ageGender,
      signature: formData.signature || rxData.signature,
    };
    if (includePrescription && rxTemplateReady) {
      generateMergedPdf(formData, rxDataForPdf, patient.doc_id);
    } else {
      generateCertificatePdf(formData, patient.doc_id);
    }
  }, [certTemplateReady, rxTemplateReady, includePrescription, formData, rxData, patient.doc_id, generateCertificatePdf, generateMergedPdf]);

  const certType = formData.certificateType;
  const showLeaveFields =
    certType === 'SICK LEAVE CERTIFICATE' ||
    certType === 'WORK FROM HOME CERTIFICATE' ||
    certType === 'RECOVERY CERTIFICATE';
  const showCaretakerFields = certType === 'CARETAKER CERTIFICATE';
  const showTravelFields = certType === 'UNFIT TO TRAVEL CERTIFICATE';

  // Active tab computed helpers
  const isDirty = activeTab === 'certificate' ? certIsDirty : rxIsDirty;
  const isSubmitting = activeTab === 'certificate' ? certIsSubmitting : rxIsSubmitting;
  const submitError = activeTab === 'certificate' ? certSubmitError : rxSubmitError;
  const saveSuccess = activeTab === 'certificate' ? certSaveSuccess : rxSaveSuccess;
  const handleReset = activeTab === 'certificate' ? certReset : rxReset;
  const handleSubmit = activeTab === 'certificate' ? certSubmit : rxSubmit;
  const saveLabel = activeTab === 'certificate' ? 'Save Certificate' : 'Save Prescription';
  const savingLabel = activeTab === 'certificate' ? 'Saving…' : 'Saving…';

  return (
    <div className="space-y-6 pb-16">
      {/* ── Page header ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {!isPublic && (
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Go back"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName}</h1>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[patient.status] || 'bg-gray-100 text-gray-700'}`}>
                {patient.status}
              </span>
              {isDirty && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  Unsaved changes
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              #{patient.doc_id} · Certificate &amp; Prescription Form
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {saveSuccess && (
            <span className="flex items-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-400">
              <CheckCircleIcon className="w-4 h-4" />
              Saved!
            </span>
          )}
          <button
            onClick={handleReset}
            disabled={isSubmitting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Reset
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                setShowPdfPanel((prev) => !prev);
                if (!showPdfPanel) fetchTemplates();
              }}
              disabled={isGenerating}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              {isGenerating ? 'Generating…' : 'Generate PDF'}
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white disabled:opacity-50 rounded-lg transition-colors shadow-sm ${
              activeTab === 'prescription'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <DocumentCheckIcon className="w-4 h-4" />
            {isSubmitting ? savingLabel : saveLabel}
          </button>
        </div>
      </div>

      {/* ── Submit error ──────────────────────────────────────────── */}
      {submitError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
          {submitError}
        </div>
      )}

      {/* ── Patient banner ────────────────────────────────────────── */}
      <PatientBanner patient={patient} displayName={displayName} />

      {/* ── Tab bar ───────────────────────────────────────────────── */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('certificate')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'certificate'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <ClipboardDocumentListIcon className="w-4 h-4" />
          Certificate Form
        </button>
        <button
          onClick={() => setActiveTab('prescription')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'prescription'
              ? 'border-emerald-600 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <ClipboardDocumentCheckIcon className="w-4 h-4" />
          Prescription
          <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
            Optional
          </span>
        </button>
      </div>

      {/* ── PDF generate panel ───────────────────────────────────── */}
      {showPdfPanel && (
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-5">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentArrowDownIcon className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Generate PDF</h3>
            </div>
            <button
              onClick={() => setShowPdfPanel(false)}
              className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Error */}
          {generateError && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
              {generateError}
            </div>
          )}

          {isLoadingTemplates ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              Loading templates…
            </div>
          ) : templateError ? (
            <p className="text-sm text-red-500">{templateError}</p>
          ) : (
            <>
              {/* Include Prescription toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIncludePrescription((p) => !p)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    includePrescription
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  <ClipboardDocumentCheckIcon className="w-4 h-4" />
                  {includePrescription ? 'Prescription included' : 'Include Prescription'}
                </button>
                {includePrescription && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400">
                    PDFs will be merged into one file
                  </span>
                )}
              </div>

              {/* Generate button */}
              <div className="flex items-center gap-3 pt-1 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleGeneratePdf}
                  disabled={isGenerating || !certTemplateReady || (includePrescription && !rxTemplateReady)}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
                >
                  <DocumentArrowDownIcon className="w-4 h-4" />
                  {isGenerating ? 'Generating…' : includePrescription ? 'Generate & Merge PDF' : 'Generate PDF'}
                </button>
                {!certTemplateReady && !isLoadingTemplates && (
                  <p className="text-xs text-red-400">Certificate template not found</p>
                )}
                {includePrescription && !rxTemplateReady && !isLoadingTemplates && (
                  <p className="text-xs text-amber-500">Prescription template not found</p>
                )}
              </div>

              {/* Save to S3 confirmation */}
              {pendingUpload && uploadState === 'idle' && (
                <div className="flex flex-col gap-2 p-3 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
                    PDF downloaded. Save to patient records?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={confirmUpload}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      Save to Records
                    </button>
                    <button
                      type="button"
                      onClick={discardUpload}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}

              {/* Upload status */}
              {uploadState === 'loading' && (
                <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                  Uploading to records…
                </div>
              )}
              {uploadState === 'success' && (
                <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Saved to patient records
                </div>
              )}
              {uploadState === 'error' && (
                <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
                  <p className="text-xs font-medium text-red-700 dark:text-red-300">
                    Upload failed — {uploadError || 'Unknown error'}
                  </p>
                  <button
                    type="button"
                    onClick={confirmUpload}
                    className="self-start px-3 py-1 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          CERTIFICATE FORM SECTIONS
      ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'certificate' && (
        <>
          {/* Section 1: Certificate Type */}
          <Section step={1} title="Certificate Type" icon={<ClipboardDocumentListIcon className="w-4 h-4" />}>
            <div className="flex flex-wrap gap-2">
              {CERTIFICATE_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setField('certificateType', type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    certType === type
                      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  {type.replace(' CERTIFICATE', '')}
                </button>
              ))}
            </div>
            {errors.certificateType && (
              <p className="mt-3 text-xs text-red-500 flex items-center gap-1">
                <ExclamationCircleIcon className="w-3.5 h-3.5" />
                {errors.certificateType}
              </p>
            )}
          </Section>

          {/* Sections 2 & 3: Doctor + Patient */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Section 2: Doctor Details */}
            <Section step={2} title="Doctor Details" icon={<IdentificationIcon className="w-4 h-4" />}>
              <div className="space-y-4">
                <Field label="Select from registered doctors" hint="Auto-fills name, reg. no., specialization and signature">
                  <select
                    value={activeDoctorId}
                    onChange={(e) => handleDoctorSelect(e.target.value)}
                    className={inputCls()}
                  >
                    <option value="">— Choose a doctor —</option>
                    {doctors.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}{d.specialization ? ` · ${d.specialization}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>

                {isSignatureFetching && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                    Fetching signature…
                  </div>
                )}
                {formData.signature && !isSignatureFetching && (
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                    <img src={formData.signature} alt="Doctor signature" className="h-10 object-contain" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Doctor Signature</p>
                      <p className="text-xs text-gray-400">Will be embedded in PDF</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setField('signature', '')}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove signature"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                <Field label="Doctor Name" error={errors.drName} required>
                  <input
                    name="drName"
                    value={formData.drName}
                    onChange={handleChange}
                    placeholder="Dr. Full Name"
                    className={inputCls(errors.drName)}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Reg. No." error={errors.regNo} required>
                    <input
                      name="regNo"
                      value={formData.regNo}
                      onChange={handleChange}
                      placeholder="Registration number"
                      className={inputCls(errors.regNo)}
                    />
                  </Field>
                  <Field label="Specialization">
                    <input
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      placeholder="e.g. General Physician"
                      className={inputCls()}
                    />
                  </Field>
                </div>
              </div>
            </Section>

            {/* Section 3: Patient Details */}
            <Section step={3} title="Patient Details" icon={<UserCircleIcon className="w-4 h-4" />}>
              <div className="space-y-4">
                <Field label="Full Name" error={errors.patientName} required>
                  <input
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleChange}
                    placeholder="Patient full name"
                    className={inputCls(errors.patientName)}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Age" error={errors.age} required>
                    <input
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      placeholder="e.g. 34"
                      className={inputCls(errors.age)}
                    />
                  </Field>
                  <Field label="Gender" error={errors.gender} required>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={inputCls(errors.gender)}
                    >
                      <option value="">— Select —</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                </div>

                {certType === 'RECOVERY CERTIFICATE' && (
                  <div className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-2.5">
                      Pronouns — used in certificate text
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="He / She">
                        <input
                          name="he_she"
                          value={formData.he_she}
                          onChange={handleChange}
                          placeholder="he / she / they"
                          className={inputCls()}
                        />
                      </Field>
                      <Field label="His / Her">
                        <input
                          name="his_her"
                          value={formData.his_her}
                          onChange={handleChange}
                          placeholder="his / her / their"
                          className={inputCls()}
                        />
                      </Field>
                      <Field label="Him / Her">
                        <input
                          name="him_her"
                          value={formData.him_her}
                          onChange={handleChange}
                          placeholder="him / her / them"
                          className={inputCls()}
                        />
                      </Field>
                    </div>
                  </div>
                )}

                <Field label="Address">
                  <input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Full residential address"
                    className={inputCls()}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Organisation / Employer">
                    <input
                      name="organisation"
                      value={formData.organisation}
                      onChange={handleChange}
                      placeholder="Company / school name"
                      className={inputCls()}
                    />
                  </Field>
                  <Field label="Identification Mark">
                    <input
                      name="identificationMark"
                      value={formData.identificationMark}
                      onChange={handleChange}
                      placeholder="e.g. Mole on right hand"
                      className={inputCls()}
                    />
                  </Field>
                </div>
              </div>
            </Section>
          </div>

          {/* Section 4: Medical Details */}
          <Section step={4} title="Medical Details" icon={<BeakerIcon className="w-4 h-4" />}>
            <div className="space-y-5">
              <div className={`grid grid-cols-1 gap-4 ${certType === 'FITNESS CERTIFICATE' ? 'sm:grid-cols-3' : 'sm:grid-cols-1'}`}>
                <Field label="Issue Date" error={errors.issueDate} required>
                  <input
                    type="date"
                    name="issueDate"
                    value={formData.issueDate}
                    onChange={handleChange}
                    className={dateCls(errors.issueDate)}
                  />
                </Field>
                {certType === 'FITNESS CERTIFICATE' && (
                  <>
                    <Field label="Examination Date">
                      <input
                        type="date"
                        name="examDate"
                        value={formData.examDate}
                        onChange={handleChange}
                        className={dateCls()}
                      />
                    </Field>
                    <Field label="Examination Time">
                      <input
                        type="time"
                        name="examTime"
                        value={formData.examTime}
                        onChange={handleChange}
                        className={inputCls()}
                      />
                    </Field>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Diagnosis" error={errors.diagnosis} required>
                  <textarea
                    name="diagnosis"
                    value={formData.diagnosis}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Primary diagnosis / clinical finding"
                    className={`${inputCls(errors.diagnosis)} resize-none`}
                  />
                </Field>
                {certType === 'FITNESS CERTIFICATE' ? (
                  <Field label="Fit Stamp (image)" hint="Stamp image embedded in the PDF">
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onloadend = () => setField('fitStamp', reader.result as string);
                          reader.readAsDataURL(file);
                        }}
                        className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
                      />
                      {formData.fitStamp && (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                          <img src={formData.fitStamp} alt="Fit stamp" className="h-12 object-contain" />
                          <button
                            type="button"
                            onClick={() => setField('fitStamp', '')}
                            className="ml-auto p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <XMarkIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </Field>
                ) : (
                  <Field label="Details of Illness">
                    <textarea
                      name="illnessDetails"
                      value={formData.illnessDetails}
                      onChange={handleChange}
                      rows={4}
                      placeholder="History and progression of illness"
                      className={`${inputCls()} resize-none`}
                    />
                  </Field>
                )}
              </div>
            </div>
          </Section>

          {/* Section 5: Leave / Recovery (conditional) */}
          {showLeaveFields && (
            <Section step={5} title="Leave / Recovery Details" icon={<DocumentTextIcon className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="From Date" error={errors.fromDate} required>
                  <input
                    type="date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    className={dateCls(errors.fromDate)}
                  />
                </Field>
                {certType !== 'RECOVERY CERTIFICATE' && (
                  <Field label="Number of Days" error={errors.noOfDays} required>
                    <input
                      name="noOfDays"
                      value={formData.noOfDays}
                      onChange={handleChange}
                      placeholder="e.g. 5"
                      className={inputCls(errors.noOfDays)}
                    />
                  </Field>
                )}
              </div>
            </Section>
          )}

          {/* Section 5: Caretaker (conditional) */}
          {showCaretakerFields && (
            <Section step={5} title="Caretaker Details" icon={<UserCircleIcon className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Caretaker Full Name" error={errors.caretakerFullName} required>
                  <input
                    name="caretakerFullName"
                    value={formData.caretakerFullName}
                    onChange={handleChange}
                    placeholder="Full name of caretaker"
                    className={inputCls(errors.caretakerFullName)}
                  />
                </Field>
                <Field label="Caretaker Age" error={errors.caretakerAge} required>
                  <input
                    name="caretakerAge"
                    value={formData.caretakerAge}
                    onChange={handleChange}
                    placeholder="Age"
                    className={inputCls(errors.caretakerAge)}
                  />
                </Field>
                <Field label="Relation to Patient" error={errors.caretakerRelation} required>
                  <input
                    name="caretakerRelation"
                    value={formData.caretakerRelation}
                    onChange={handleChange}
                    placeholder="e.g. Spouse, Parent, Child"
                    className={inputCls(errors.caretakerRelation)}
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* Section 5: Unfit to Travel (conditional) */}
          {showTravelFields && (
            <Section step={5} title="Travel Restriction Details" icon={<DocumentTextIcon className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Starting From Date" error={errors.fromDate} required>
                  <input
                    type="date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    className={dateCls(errors.fromDate)}
                  />
                </Field>
                <Field label="Number of Days" error={errors.noOfDays} required>
                  <input
                    name="noOfDays"
                    value={formData.noOfDays}
                    onChange={handleChange}
                    placeholder="e.g. 5"
                    className={inputCls(errors.noOfDays)}
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* Section 5: Medical Certificate validity (conditional) */}
          {certType === 'MEDICAL CERTIFICATE' && (
            <Section step={5} title="Certificate Validity" icon={<DocumentTextIcon className="w-4 h-4" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Validity Period" error={errors.validity} hint="e.g. Valid for 3 months from issue date">
                  <input
                    name="validity"
                    value={formData.validity}
                    onChange={handleChange}
                    placeholder="e.g. 3 months / 6 weeks"
                    className={inputCls(errors.validity)}
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* Section 6: Additional Details */}
          <Section step={6} title="Additional Details" icon={<DocumentTextIcon className="w-4 h-4" />}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="To (Addressee)">
                  <input
                    name="to"
                    value={formData.to}
                    onChange={handleChange}
                    placeholder="e.g. The HR Manager"
                    className={inputCls()}
                  />
                </Field>
                <Field label="Guardian / Attendant Name">
                  <input
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleChange}
                    placeholder="Guardian or attendant"
                    className={inputCls()}
                  />
                </Field>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                  Certificate Body / Content
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left: auto-generated preview */}
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Auto-generated Preview
                    </span>
                    <div className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed min-h-[220px]">
                      {generatedContent || <span className="text-gray-400 italic">Select a certificate type to preview…</span>}
                    </div>
                  </div>
                  {/* Right: editable content */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Certificate Content
                        {isContentEditing && (
                          <span className="ml-1 text-orange-500">(edited)</span>
                        )}
                      </span>
                      {isContentEditing && (
                        <button
                          type="button"
                          onClick={resetCertToAuto}
                          className="text-xs text-blue-500 hover:underline"
                        >
                          Reset to auto
                        </button>
                      )}
                    </div>
                    <textarea
                      name="content"
                      value={formData.content ?? generatedContent}
                      onChange={handleCertContentChange}
                      rows={10}
                      placeholder="Certificate content will be auto-generated…"
                      className={`${inputCls()} resize-y`}
                    />
                  </div>
                </div>
              </div>

              <Field label="Additional Notes">
                <textarea
                  name="additionalDetails"
                  value={formData.additionalDetails}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any remarks or supplementary information"
                  className={`${inputCls()} resize-none`}
                />
              </Field>
            </div>
          </Section>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          PRESCRIPTION FORM SECTIONS
      ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'prescription' && (
        <>
          {/* Prescriber info bar */}
          <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800 rounded-xl px-6 py-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mr-2">Dr.</span>
                <span className="font-medium text-gray-800 dark:text-gray-100">{rxData.drName || formData.drName || '—'}</span>
              </div>
              {(rxData.regNo || formData.regNo) && (
                <span className="text-gray-500 dark:text-gray-400">
                  Reg. {rxData.regNo || formData.regNo}
                </span>
              )}
              {(rxData.specialization || formData.specialization) && (
                <span className="text-gray-500 dark:text-gray-400">
                  {rxData.specialization || formData.specialization}
                </span>
              )}
              <span className="ml-auto text-xs text-gray-400">{rxData.issueDate}</span>
              <p className="w-full text-xs text-emerald-600 dark:text-emerald-400 -mt-0.5">
                Doctor details are synced from the Certificate Form. Use the "Select Doctor" dropdown there to update.
              </p>
            </div>
          </div>

          {/* Section P1: Chief Complaints & History */}
          <Section step={1} title="Chief Complaints & Symptoms" icon={<BeakerIcon className="w-4 h-4" />} accent="emerald">
            <div className="space-y-4">
              <Field label="Chief Complaints (C/O)" required hint="Main symptoms the patient presents with">
                <textarea
                  name="complaintsOf"
                  value={rxData.complaintsOf}
                  onChange={rxHandleChange}
                  rows={3}
                  placeholder="e.g. Fever, headache, body ache for 3 days"
                  className={`${inputCls(!rxData.complaintsOf && rxSubmitError ? 'error' : '')} resize-none`}
                />
              </Field>

              <Field label="History of Present Illness (H/O)">
                <textarea
                  name="historyOfSymptoms"
                  value={rxData.historyOfSymptoms}
                  onChange={rxHandleChange}
                  rows={3}
                  placeholder="Onset, duration, progression of symptoms"
                  className={`${inputCls()} resize-none`}
                />
              </Field>

              <Field label="Intensity of Symptoms">
                <div className="flex gap-2 mt-1">
                  {(['Mild', 'Moderate', 'Severe'] as const).map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => rxSetField('intensityOfSymptoms', level)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        rxData.intensityOfSymptoms === level
                          ? level === 'Mild'
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : level === 'Moderate'
                            ? 'bg-amber-500 border-amber-500 text-white'
                            : 'bg-red-500 border-red-500 text-white'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Concurrent Illnesses">
                <input
                  name="concurrentIllnesses"
                  value={rxData.concurrentIllnesses}
                  onChange={rxHandleChange}
                  placeholder="Any other active conditions"
                  className={inputCls()}
                />
              </Field>
            </div>
          </Section>

          {/* Section P2: Medical History */}
          <Section step={2} title="Medical History" icon={<DocumentTextIcon className="w-4 h-4" />} accent="emerald">
            <div className="divide-y divide-gray-50 dark:divide-gray-800">
              <HistoryRow
                label="Past history of similar symptoms"
                value={rxData.pastHistoryOfSimilarSymptoms}
                onToggle={(v) => rxSetField('pastHistoryOfSimilarSymptoms', v)}
                detailName="pastHistoryDetails"
                detailValue={rxData.pastHistoryDetails}
                onDetailChange={rxHandleChange}
              />
              <HistoryRow
                label="History of surgery"
                value={rxData.historyOfSurgery}
                onToggle={(v) => rxSetField('historyOfSurgery', v)}
                detailName="surgeryDetails"
                detailValue={rxData.surgeryDetails}
                onDetailChange={rxHandleChange}
                detailPlaceholder="Type of surgery, year"
              />
              <HistoryRow
                label="History of travel (last 14 days)"
                value={rxData.historyOfTravel}
                onToggle={(v) => rxSetField('historyOfTravel', v)}
                detailName="travelDetails"
                detailValue={rxData.travelDetails}
                onDetailChange={rxHandleChange}
                detailPlaceholder="Destination(s)"
              />
              <HistoryRow
                label="Relevant family history"
                value={rxData.relevantFamilyHistory}
                onToggle={(v) => rxSetField('relevantFamilyHistory', v)}
                detailName="familyHistoryDetails"
                detailValue={rxData.familyHistoryDetails}
                onDetailChange={rxHandleChange}
                detailPlaceholder="Condition and relation"
              />
              <HistoryRow
                label="Substance intake (alcohol / tobacco / drugs)"
                value={rxData.substanceIntake}
                onToggle={(v) => rxSetField('substanceIntake', v)}
                detailName="substanceIntakeDetails"
                detailValue={rxData.substanceIntakeDetails}
                onDetailChange={rxHandleChange}
                detailPlaceholder="Substance type and frequency"
              />
              <HistoryRow
                label="Self-medication / home remedies taken"
                value={rxData.selfMedicationHomeRemedies}
                onToggle={(v) => rxSetField('selfMedicationHomeRemedies', v)}
                detailName="selfMedicationDetails"
                detailValue={rxData.selfMedicationDetails}
                onDetailChange={rxHandleChange}
                detailPlaceholder="Medications or remedies used"
              />
            </div>
            <div className="mt-4">
              <Field label="Significant Past Medical History">
                <textarea
                  name="significantPastHistory"
                  value={rxData.significantPastHistory}
                  onChange={rxHandleChange}
                  rows={2}
                  placeholder="Chronic conditions, major illnesses, allergies"
                  className={`${inputCls()} resize-none`}
                />
              </Field>
            </div>
          </Section>

          {/* Section P3: Diagnosis & Prescription */}
          <Section step={3} title="Diagnosis & Prescription (Rx)" icon={<ClipboardDocumentCheckIcon className="w-4 h-4" />} accent="emerald">
            <div className="space-y-6">
              <Field label="Full Diagnosis" required hint="Clinical diagnosis after examination">
                <textarea
                  name="fullDiagnosis"
                  value={rxData.fullDiagnosis}
                  onChange={rxHandleChange}
                  rows={3}
                  placeholder="Complete diagnosis including ICD codes if applicable"
                  className={`${inputCls(!rxData.fullDiagnosis && rxSubmitError ? 'error' : '')} resize-none`}
                />
              </Field>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                  Prescription Lines (Rx)
                </p>
                <div className="space-y-2">
                  {rxData.rxLines.map((line, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 w-8 flex-shrink-0 text-right">
                        {idx + 1}.
                      </span>
                      <input
                        value={line}
                        onChange={(e) => setRxLine(idx, e.target.value)}
                        placeholder={`e.g. Tab Paracetamol 500mg BD × 5 days`}
                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                      <button
                        type="button"
                        onClick={() => removeRxLine(idx)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                        title="Remove line"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addRxLine}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add Rx Line
                </button>
              </div>
            </div>
          </Section>

          {/* Section P4: Advice & Notes */}
          <Section step={4} title="Advice & Special Notes" icon={<DocumentTextIcon className="w-4 h-4" />} accent="emerald">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Advice by Doctor (RMP)" hint="Follow-up, lifestyle, diet, rest">
                <textarea
                  name="adviceByRMP"
                  value={rxData.adviceByRMP}
                  onChange={rxHandleChange}
                  rows={4}
                  placeholder="e.g. Complete rest, plenty of fluids, avoid spicy food, follow-up after 5 days"
                  className={`${inputCls()} resize-none`}
                />
              </Field>
              <Field label="Special Notes / Remarks">
                <textarea
                  name="specialNotesByRMP"
                  value={rxData.specialNotesByRMP}
                  onChange={rxHandleChange}
                  rows={4}
                  placeholder="Any special instructions or clinical observations"
                  className={`${inputCls()} resize-none`}
                />
              </Field>
            </div>
          </Section>
        </>
      )}

      {/* ── Footer actions ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        {!isPublic ? (
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors flex items-center gap-1"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Back to Patients
          </button>
        ) : <div />}
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            Reset Form
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center gap-2 px-6 py-2 text-sm font-medium text-white disabled:opacity-50 rounded-lg transition-colors shadow-sm ${
              activeTab === 'prescription'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <DocumentCheckIcon className="w-4 h-4" />
            {isSubmitting ? savingLabel : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Page container ───────────────────────────────────────────────────────────

export const CertificateFormPage: React.FC = () => {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.groups?.includes('admin') ?? false;
  const { patient, isLoading, error, initialFormData, submittedPrescription } = useSelectedPatient(docId);

  if (isLoading) return <LoadingSkeleton />;

  if (error || !patient) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <p className="text-gray-500 dark:text-gray-400 text-base">
          {error || 'Patient not found.'}
        </p>
        <button
          onClick={() => navigate('/patients')}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium flex items-center gap-1"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Patients
        </button>
      </div>
    );
  }

  const displayName =
    [patient.name, patient.last_name].filter(Boolean).join(' ').trim() ||
    patient.full_name ||
    'Unknown Patient';

  return (
    <CertificateFormInner
      key={patient.doc_id}
      patient={patient}
      initialFormData={initialFormData}
      displayName={displayName}
      isAdmin={isAdmin}
      submittedPrescription={submittedPrescription}
    />
  );
};
